import { useMsal } from "@azure/msal-react";
import {
	ChevronLeftOutlined as CollapseIcon,
	ChevronRightOutlined as ExpandIcon,
	ExpandLess,
	ExpandMore,
	LogoutOutlined as LogoutIcon,
	SettingsOutlined as SettingsIcon
} from "@mui/icons-material";
import {
	Avatar,
	Box,
	Collapse,
	Divider,
	Drawer,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Tooltip,
	Typography,
	Zoom
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
	getUserProfileSectionStyles,
	getUserProfileContentStyles,
	getUserProfileInnerStyles
} from "./shared/UserProfileSectionStyles";
import { useNavigationInterceptorContext } from "../../contexts/useNavigationInterceptorContext";
import { zoullooColors } from "../../theme";
import { stringAvatar } from "../../utils/avatarUtils";
import { UserInformationViewModel } from "../../ViewModels/UserInformationViewModel";
import { setStorageItem } from "../../hooks/useEnvironmentStorage";

const getMenuItemFromRoute = (pathname: string) => {
	if (pathname.startsWith("/settings")) {
		return "settings";
	}

	return "";
};

interface MenuProps {
	onClose?: () => void;
	variant?: "permanent" | "persistent" | "temporary";
	isCollapsed: boolean;
	setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
	drawerWidth: number;
	defaultActiveItem: string;
	userInformationViewModel: UserInformationViewModel;
	lastActiveMenuStorageKey: string;
	mainMenuItems: MenuItem[];
}

export interface MenuItem {
	key: string;
	text: React.ReactNode;
	icon: React.ReactNode;
	onClick?: () => void | Promise<void>;
	submenu?: MenuItem[];
	disableHighlight?: boolean;
	disableClick?: boolean;
	activateClickOnActiveItem?: boolean;
}

const Menu: React.FC<MenuProps> = ({
	onClose,
	variant = "permanent",
	isCollapsed,
	setIsCollapsed,
	drawerWidth,
	defaultActiveItem,
	userInformationViewModel,
	lastActiveMenuStorageKey,
	mainMenuItems
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	// Only show settings in the bottom menu (Messages, Notifications, Support are hidden for all roles)
	const bottomMenuItems: MenuItem[] = [
		{
			key: "settings",
			text: t("menu.settings"),
			icon: <SettingsIcon />,
			onClick: () => {
				sessionStorage.setItem(lastActiveMenuStorageKey, "settings");
				navigate("/settings");
				setActiveMenuItem("settings");
			}
		}
	];

	const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
	const [tooltipOpen, setTooltipOpen] = React.useState<string | null>(null);

	const { isNavigationBlocked } = useNavigationInterceptorContext();

	const [activeMenuItem, setActiveMenuItem] = useState("");
	const { instance } = useMsal();
	const { interceptNavigation } = useNavigationInterceptorContext();
	const location = useLocation();

	const getInitialActiveMenuItem = useCallback(() => {
		const routeBasedItem = getMenuItemFromRoute(location.pathname);
		const storedItem = sessionStorage.getItem(lastActiveMenuStorageKey);

		if (!storedItem || routeBasedItem !== storedItem) {
			sessionStorage.setItem(lastActiveMenuStorageKey, routeBasedItem);

			return routeBasedItem;
		}

		return storedItem;
	}, [location.pathname, userInformationViewModel.userId]);
	useEffect(() => {
		const initialActiveItem = getInitialActiveMenuItem() || defaultActiveItem;

		setActiveMenuItem(initialActiveItem);
	}, [location.pathname, defaultActiveItem, getInitialActiveMenuItem]);

	const handleLogoutRedirect = () => {
		interceptNavigation({
			onValidNavigation: () => {
				instance.logoutRedirect({
					postLogoutRedirectUri: "/"
				});
			},
			onConfirmNavigation: () => {
				instance.logoutRedirect({
					postLogoutRedirectUri: "/"
				});
			}
		});
	};

	const handleMenuToggle = () => {
		const newCollapsedState = !isCollapsed;

		setIsCollapsed(newCollapsedState);

		if (userInformationViewModel.userId) {
			const storageKey = `zoulloo_menu_collapsed_user_${userInformationViewModel.userId}`;
			setStorageItem(storageKey, newCollapsedState.toString());
		}
	};

	// Custom avatar function for menu with specific styling
	const getMenuAvatar = (firstName?: string, lastName?: string) => {
		const baseAvatar = stringAvatar(firstName, lastName, 42);
		return {
			...baseAvatar,
			sx: {
				...baseAvatar.sx,
				"&:hover": {
					cursor: isCollapsed ? "pointer" : "default"
				}
			}
		};
	};

	useEffect(() => {
		localStorage.setItem(
			`zoulloo_last_visited_path_${userInformationViewModel.userId}`,
			location.pathname
		);
	}, [location.pathname]);

	useEffect(() => {
		// Recursively find all parent keys that contain the active menu item
		const findParentKeys = (items: MenuItem[], activeKey: string): string[] => {
			const parentKeys: string[] = [];
			for (const item of items) {
				if (item.submenu && isActiveInSubtree(item.submenu, activeKey)) {
					parentKeys.push(item.key, ...findParentKeys(item.submenu, activeKey));
				}
			}
			return parentKeys;
		};

		const parentKeys = findParentKeys(mainMenuItems, activeMenuItem);
		setExpanded(new Set(parentKeys));
	}, [activeMenuItem]);

	// Helper function to recursively check if an item contains the active child
	const isActiveInSubtree = (items: MenuItem[] | undefined, activeKey: string): boolean => {
		if (!items) return false;
		return items.some(
			(item: MenuItem) => item.key === activeKey || isActiveInSubtree(item.submenu, activeKey)
		);
	};

	// Helper function to determine transform styles
	const getTransformStyle = (isBottomMenu: boolean, isCollapsed: boolean): string => {
		if (!isBottomMenu) return "none";
		return isCollapsed ? "scale(0.95) translateY(2px)" : "scale(1) translateX(0)";
	};

	// Helper function to determine padding left
	const getPaddingLeft = (
		isCollapsed: boolean,
		isBottomMenu: boolean,
		isSubmenu: boolean,
		level: number = 0
	): string => {
		if (isCollapsed || isBottomMenu) return "0px";
		if (isSubmenu) return `${39 + (level - 1) * 15}px`;
		return "24px";
	};

	// Helper function to determine menu item styles
	const getMenuItemStyles = (
		item: MenuItem,
		isActive: boolean,
		isParentOfActiveChild: boolean | undefined,
		isSubmenu: boolean
	) => {
		const shouldHighlight =
			(isActive && !item.disableHighlight) ||
			(Boolean(isParentOfActiveChild) && !item.disableHighlight);

		let fontWeight: number;
		if (shouldHighlight) {
			fontWeight = 600;
		} else if (isSubmenu) {
			fontWeight = 400;
		} else {
			fontWeight = 500;
		}

		return { shouldHighlight, fontWeight };
	};

	// Helper function to handle menu item click logic
	const handleMenuItemClickLogic = (
		item: MenuItem,
		isSubmenu: boolean,
		isBottomMenu: boolean
	) => {
		if (isNavigationBlocked) {
			// No optimistic highlight is set on this path; the handler shows its own
			// feedback on failure, so only prevent an unhandled rejection here. The async
			// IIFE invokes the handler synchronously while turning a sync throw into a
			// rejection too.
			(async () => item.onClick?.())().catch(() => undefined);
			return;
		}

		// Set active menu item BEFORE calling onClick
		// This ensures the menu updates immediately
		if (!isBottomMenu && item.onClick) {
			const previousActiveItem = activeMenuItem;
			setActiveMenuItem(item.key);

			// The highlight above is optimistic: if the handler rejects (or throws), the
			// navigation did not happen, so put the highlight back — unless a later click
			// already moved it to another item (a stale revert must not clobber it).
			(async () => item.onClick!())().catch(() => {
				setActiveMenuItem(current =>
					current === item.key ? previousActiveItem : current
				);
			});
		} else {
			item.onClick?.();
		}

		setTooltipOpen(null);

		if (isBottomMenu) return;

		if (item.submenu && !isCollapsed) {
			// Item has a submenu — toggle expansion (whether or not it has onClick)
			setExpanded(prev => {
				const next = new Set(prev);
				if (item.onClick) {
					// Clickable parent (has onClick + submenu): always keep open
					next.add(item.key);
				} else {
					// Pure parent (no onClick): toggle open/close
					if (next.has(item.key)) {
						next.delete(item.key);
					} else {
						next.add(item.key);
					}
				}
				return next;
			});
			if (!item.onClick) return;
		}

		// Only set active if not already set above
		if (!item.onClick || isBottomMenu) {
			setActiveMenuItem(item.key);
		}

		if (!item.onClick && !isSubmenu) {
			setExpanded(new Set());
		}
	};

	// Helper function to handle expansion toggle
	const handleExpansionToggle = (item: MenuItem, event: React.MouseEvent) => {
		event.stopPropagation();

		if (!item.submenu || isCollapsed) return;

		setExpanded(prev => {
			const next = new Set(prev);
			if (next.has(item.key)) {
				next.delete(item.key);
			} else {
				next.add(item.key);
			}
			return next;
		});
	};

	// Helper function to render vertical line for submenu items
	const renderSubmenuVerticalLine = (isSubmenu: boolean, isCollapsed: boolean, level: number = 1) => {
		if (!isSubmenu || isCollapsed) return null;

		return (
			<Box
				sx={{
					position: "absolute",
					left: `${32 + (level - 1) * 15}px`,
					top: 0,
					bottom: 0,
					width: "1px",
					backgroundColor: zoullooColors.border.lighter,
					zIndex: 1
				}}
			/>
		);
	};

	// Helper function to render menu item icon
	const renderMenuItemIcon = (
		item: MenuItem,
		shouldHighlight: boolean,
		isBottomMenu: boolean
	) => {
		if (!item.icon) return null;

		return (
			<Tooltip
				disableInteractive
				title={isBottomMenu ? item.text : ""}
				placement="top"
				arrow
				open={tooltipOpen === item.key}
				slots={{
					transition: Zoom
				}}
				slotProps={{
					transition: { timeout: 200 },
					popper: {
						modifiers: [
							{
								name: "offset",
								options: {
									offset: [0, -7]
								}
							}
						]
					}
				}}
			>
				<ListItemIcon
					onMouseEnter={() => {
						if (isBottomMenu) {
							setTooltipOpen(item.key);
						}
					}}
					onMouseLeave={() => setTooltipOpen(null)}
					sx={{
						height: "40px",
						color: shouldHighlight
							? zoullooColors.secondary.dark
							: zoullooColors.text.primary,
						backgroundColor: shouldHighlight
							? zoullooColors.background.menu
							: "transparent",
						border: `1px solid ${
							shouldHighlight ? zoullooColors.secondary.dark : "transparent"
						}`,
						borderRight:
							isBottomMenu || isCollapsed
								? `1px solid ${
										shouldHighlight ? zoullooColors.secondary.dark : "transparent"
								  }`
								: `0px`,
						borderRadius: isBottomMenu || isCollapsed ? `7px` : `0px`,
						minWidth: isCollapsed ? "auto" : "24px",
						padding: "7px 10px 5px 10px",
						borderTopLeftRadius: "7px",
						borderBottomLeftRadius: "7px",
						justifyContent: "center",
						"& .MuiSvgIcon-root": {
							fontSize: "1.5rem"
						}
					}}
				>
					{item.icon}
				</ListItemIcon>
			</Tooltip>
		);
	};

	// Helper function to render menu item text
	const renderMenuItemText = (
		item: MenuItem,
		shouldHighlight: boolean,
		isSubmenu: boolean,
		isParentOfActiveChild: boolean | undefined,
		fontWeight: number,
		isBottomMenu: boolean
	) => {
		if (isCollapsed || isBottomMenu) return null;

		return (
			<ListItemText
				primary={
					<Box
						sx={{
							width: "100%",
							overflow: "hidden",
							position: "relative"
						}}
					>
						<Box
							component="span"
							sx={{
								display: "inline-block",
								fontSize: "0.9rem",
								fontWeight: fontWeight,
								color: shouldHighlight
									? zoullooColors.secondary.dark
									: zoullooColors.text.primary,
								lineHeight: 1.43,
								whiteSpace: "nowrap",
								textOverflow: "ellipsis",
								overflow: "hidden",
								maxWidth: isSubmenu ? "199px" : "172px",
								height: "31px",
								...(typeof item.text === "string" &&
									((isSubmenu && item.text.length > (shouldHighlight ? 26 : 27)) ||
										(!isSubmenu && item.text.length > (shouldHighlight ? 23 : 24))) && {
										"&:hover": {
											animation: "scroll-contained 4s ease-in-out infinite",
											textOverflow: "unset",
											maxWidth: "unset"
										}
									})
							}}
						>
							{item.text}
						</Box>
					</Box>
				}
				sx={{
					height: "40px",
					border: `1px solid ${
						shouldHighlight ? zoullooColors.secondary.dark : "transparent"
					}`,
					borderRight:
						isSubmenu || !item.submenu
							? `1px solid ${
									shouldHighlight ? zoullooColors.secondary.dark : "transparent"
							  }`
							: `0px`,
					borderLeft: isSubmenu
						? `1px solid ${
								shouldHighlight ? zoullooColors.secondary.dark : "transparent"
						  }`
						: `0px`,
					backgroundColor: shouldHighlight
						? zoullooColors.background.menu
						: "transparent",
					paddingY: "9px",
					margin: 0,
					borderTopRightRadius:
						isParentOfActiveChild || (item.submenu && item.submenu.length > 0)
							? "0px"
							: "7px",
					borderBottomRightRadius:
						isParentOfActiveChild || (item.submenu && item.submenu.length > 0)
							? "0px"
							: "7px",
					borderTopLeftRadius: item.icon ? "0px" : "7px",
					borderBottomLeftRadius: item.icon ? "0px" : "7px",
					px: item.icon ? "0px" : "11px",
					"@keyframes scroll-contained": {
						"0%": {
							transform: "translateX(0)",
							opacity: 1
						},
						"15%": {
							transform: "translateX(0)",
							opacity: 1
						},
						"65%": {
							transform: "translateX(calc(-100% + 120px))",
							opacity: 1
						},
						"80%": {
							transform: "translateX(calc(-100% + 120px))",
							opacity: 0.3
						},
						"90%": {
							transform: "translateX(calc(-100% + 120px))",
							opacity: 0
						},
						"95%": {
							transform: "translateX(0)",
							opacity: 0
						},
						"100%": {
							transform: "translateX(0)",
							opacity: 1
						}
					}
				}}
			/>
		);
	};

	// Helper function to render expansion icon
	const renderExpansionIcon = (item: MenuItem, shouldHighlight: boolean) => {
		if (!item.submenu) return null;

		return (
			<IconButton
				disableRipple
				size="medium"
				onClick={e => handleExpansionToggle(item, e)}
				sx={{
					height: "40px",
					borderTopRightRadius: "7px",
					borderBottomRightRadius: "7px",
					borderTopLeftRadius: "0px",
					borderBottomLeftRadius: "0px",
					border: `1px solid ${
						shouldHighlight ? zoullooColors.secondary.dark : "transparent"
					}`,
					borderLeft: `0px`,
					backgroundColor: shouldHighlight
						? zoullooColors.background.menu
						: "transparent",
					color: shouldHighlight
						? zoullooColors.secondary.dark
						: zoullooColors.text.primary,
					padding: "6.5px",
					marginLeft: "auto",
					pointerEvents: "auto"
				}}
			>
				{expanded.has(item.key) ? <ExpandLess /> : <ExpandMore />}
			</IconButton>
		);
	};

	// Helper function to get ListItemButton styles
	const getListItemButtonStyles = (
		shouldHighlight: boolean,
		isBottomMenu: boolean,
		isCollapsed: boolean,
		paddingLeft: string,
		item: MenuItem,
		isSubmenu: boolean
	) => {
		return {
			height: "40px",
			padding: "0",
			minHeight: 48,
			paddingLeft: paddingLeft,
			paddingRight: isCollapsed || isBottomMenu ? "0px" : "24px",
			borderRadius: "8px",
			color: shouldHighlight ? zoullooColors.grey[900] : zoullooColors.grey[550],
			justifyContent: isCollapsed || isBottomMenu ? "center" : "flex-start",
			alignItems: "center",
			"&:hover .MuiListItemIcon-root": {
				color: zoullooColors.grey[900],
				backgroundColor: zoullooColors.background.menu
			},
			"&:hover .MuiBox-root": {
				color: zoullooColors.grey[900]
			},
			"&:hover .MuiListItemText-root": {
				borderRadius: isSubmenu ? "8px" : "0px",
				borderTopRightRadius: item.submenu ? "0px" : "8px",
				borderBottomRightRadius: item.submenu ? "0px" : "8px",
				backgroundColor: zoullooColors.background.menu
			},
			"&:hover .MuiIconButton-root": {
				backgroundColor: zoullooColors.background.menu
			},
			"&:hover": {
				backgroundColor: "transparent !important"
			},
			transition: isBottomMenu ? "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)" : "all 1s ease",
			transform: isBottomMenu && isCollapsed ? "scale(0.9)" : "scale(1)",
			margin: isBottomMenu && isCollapsed ? "4px 0" : "0",
			cursor:
				activeMenuItem === item.key && !item.activateClickOnActiveItem
					? "default"
					: "pointer"
		};
	};

	// Helper function to render list item button content
	const renderListItemButtonContent = (
		item: MenuItem,
		shouldHighlight: boolean,
		isSubmenu: boolean,
		isParentOfActiveChild: boolean | undefined,
		fontWeight: number,
		isBottomMenu: boolean
	) => {
		return (
			<>
				{renderMenuItemIcon(item, shouldHighlight, isBottomMenu)}
				{renderMenuItemText(
					item,
					shouldHighlight,
					isSubmenu,
					isParentOfActiveChild,
					fontWeight,
					isBottomMenu
				)}
				{!isCollapsed && renderExpansionIcon(item, shouldHighlight)}
			</>
		);
	};

	const renderMenuItem = (item: MenuItem, isSubmenu = false, isBottomMenu = false, level = 0) => {
		const isActive = activeMenuItem === item.key;
		const isParentOfActiveChild = item.submenu
			? isActiveInSubtree(item.submenu, activeMenuItem)
			: false;
		const { shouldHighlight, fontWeight } = getMenuItemStyles(
			item,
			isActive,
			isParentOfActiveChild,
			isSubmenu
		);

		if (isCollapsed && isSubmenu) {
			return null;
		}

		const transformStyle = getTransformStyle(isBottomMenu, isCollapsed);
		const paddingLeft = getPaddingLeft(isCollapsed, isBottomMenu, isSubmenu, level);
		const buttonStyles = getListItemButtonStyles(
			shouldHighlight,
			isBottomMenu,
			isCollapsed,
			paddingLeft,
			item,
			isSubmenu
		);

		// Calculate margin bottom based on menu type and collapsed state
		let marginBottom: string;
		if (isBottomMenu && isCollapsed) {
			marginBottom = "4px";
		} else if (isSubmenu) {
			marginBottom = "0px";
		} else {
			marginBottom = "5px";
		}

		return (
			<ListItem
				key={item.key}
				disablePadding
				sx={{
					height: "45px",
					position: "relative",
					transition: isBottomMenu ? "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)" : "none",
					width: "100%",
					marginBottom,
					transform: transformStyle
				}}
			>
				{renderSubmenuVerticalLine(isSubmenu, isCollapsed, level)}

				<ListItemButton
					data-testid="menu-item"
					disableRipple
					onClick={() => {
						if (
							(activeMenuItem === item.key && !item.activateClickOnActiveItem) ||
							item.disableClick
						)
							return;

						handleMenuItemClickLogic(item, isSubmenu, isBottomMenu);
					}}
					disabled={item.disableClick}
					sx={buttonStyles}
				>
					{renderListItemButtonContent(
						item,
						shouldHighlight,
						isSubmenu,
						isParentOfActiveChild,
						fontWeight,
						isBottomMenu
					)}
				</ListItemButton>
			</ListItem>
		);
	};

	// Helper function to render user profile section
	const renderUserProfileSection = () => {
		return (
			<Box sx={getUserProfileSectionStyles(isCollapsed)}>
				<Box sx={getUserProfileContentStyles(isCollapsed)}>
					<Box sx={getUserProfileInnerStyles()}>
						<Avatar
							className="user-avatar"
							{...getMenuAvatar(
								userInformationViewModel.firstName ?? "",
								userInformationViewModel.lastName ?? ""
							)}
							src={userInformationViewModel.profilePhotoUrl}
							onClick={isCollapsed ? handleLogoutRedirect : undefined}
						/>
						{isCollapsed && (
							<Box
								sx={{
									position: "absolute",
									top: "50%",
									left: "50%",
									transform: "translate(-50%, -50%) scale(0.3)",
									color: zoullooColors.grey[700],
									bgcolor: "rgba(239, 239, 239, 0.9)",
									borderRadius: "50%",
									opacity: 0,
									alignItems: "center",
									justifyContent: "center",
									width: "43px",
									height: "43px",
									display: "flex",
									pointerEvents: "none",
									transition:
										"opacity 0.5s cubic-bezier(.4,2,.6,1), transform 0.5s cubic-bezier(.4,2,.6,1)",
									".user-avatar:hover ~ &": {
										opacity: 1,
										transform: "translate(-50%, -50%) scale(1)"
									}
								}}
							>
								<LogoutIcon fontSize="medium" />
							</Box>
						)}
						{!isCollapsed && (
							<Box sx={{ minWidth: 0 }}>
								<Box
									sx={{
										overflow: "hidden",
										position: "relative",
										width: "100%",
										lineHeight: "1",
										maxWidth: "176px"
									}}
								>
									<Typography
										component="span"
										sx={{
											display: "inline-block",
											color: zoullooColors.secondary.dark,
											fontWeight: 500,
											fontSize: "0.85rem",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
											maxWidth: "176px",
											overflow: "hidden",
											...(`${userInformationViewModel.firstName} ${userInformationViewModel.lastName}`
												.length > 25 && {
												"&:hover": {
													animation: "scroll-contained-user 4s ease-in-out infinite",
													maxWidth: "unset"
												}
											})
										}}
									>
										{`${userInformationViewModel.firstName} ${userInformationViewModel.lastName}`}
									</Typography>
								</Box>
								<Typography
									sx={{
										color: zoullooColors.grey[700],
										fontSize: "0.85rem",
										"@keyframes scroll-contained-user": {
											"0%": {
												transform: "translateX(0)",
												opacity: 1
											},
											"15%": {
												transform: "translateX(0)",
												opacity: 1
											},
											"65%": {
												transform: "translateX(calc(-100% + 140px))",
												opacity: 1
											},
											"80%": {
												transform: "translateX(calc(-100% + 140px))",
												opacity: 0.3
											},
											"90%": {
												transform: "translateX(calc(-100% + 140px))",
												opacity: 0
											},
											"95%": {
												transform: "translateX(0)",
												opacity: 0
											},
											"100%": {
												transform: "translateX(0)",
												opacity: 1
											}
										}
									}}
								>
									{userInformationViewModel.type ?? ""}
								</Typography>
							</Box>
						)}
					</Box>
					{!isCollapsed && (
						<IconButton
							sx={{
								marginRight: "4px",
								color: zoullooColors.grey[500],
								padding: "4px",
								"&:hover": {
									color: zoullooColors.secondary.dark,
									backgroundColor: "transparent"
								}
							}}
							onClick={handleLogoutRedirect}
						>
							<LogoutIcon fontSize="medium" />
						</IconButton>
					)}
				</Box>
			</Box>
		);
	};

	// Helper function to render company header section
	const renderCompanyHeader = () => {
		return (
			<Box
				sx={{
					padding: isCollapsed ? "36px 0px" : "50px 0px",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					transition: "all 0.3s ease"
				}}
			>
				<Box
					sx={{
						position: "relative",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						width: isCollapsed ? "51px" : "210px",
						height: "auto",
						transition: "width 0.3s ease"
					}}
				>
					<Box
						component="img"
						src={"../../assets/images/logo.png"}
						alt="Zoulloo Logo"
						sx={{
							position: "absolute",
							width: "210px",
							opacity: isCollapsed ? 0 : 1,
							transform: isCollapsed ? "scale(0.8)" : "scale(1)",
							transition: "opacity 0.3s ease, transform 0.3s ease"
						}}
					/>
					<Box
						component="img"
						src={"../../assets/images/zoulloo_alternative_logo.webp"}
						alt="Zoulloo Logo"
						sx={{
							position: "absolute",
							width: "51px",
							opacity: isCollapsed ? 1 : 0,
							transform: isCollapsed ? "scale(1)" : "scale(0.8)",
							transition: "opacity 0.3s ease, transform 0.3s ease"
						}}
					/>
				</Box>
				<IconButton
					onClick={handleMenuToggle}
					size="small"
					sx={{
						color: zoullooColors.grey[400],
						padding: "0",
						"&:hover": {
							backgroundColor: zoullooColors.background.light,
							color: zoullooColors.grey[700]
						},
						transition: "all 0.3s ease",
						border: `1px solid ${zoullooColors.grey[400]}`,
						position: "fixed",
						top: "51px",
						right: isCollapsed ? "calc(100% - 63px)" : "calc(100% - 300px)",
						background: zoullooColors.background.default
					}}
				>
					{isCollapsed ? <ExpandIcon /> : <CollapseIcon />}
				</IconButton>
			</Box>
		);
	};

	// Recursive submenu renderer
	const renderSubmenu = (item: MenuItem, level: number): React.ReactNode => {
		if (!item.submenu || isCollapsed) return null;

		return (
			<Collapse in={expanded.has(item.key)} timeout="auto" unmountOnExit>
				<List component="div" disablePadding>
					{item.submenu.map((subItem: MenuItem) => (
						<React.Fragment key={subItem.key}>
							{renderMenuItem(subItem, true, false, level)}
							{renderSubmenu(subItem, level + 1)}
						</React.Fragment>
					))}
				</List>
			</Collapse>
		);
	};

	const drawerContent = (
		<Box
			sx={{
				overflow: "hidden",
				width: drawerWidth,
				height: "100vh",
				display: "flex",
				flexDirection: "column",
				transition: "width 0.3s ease"
			}}
		>
			{/* Company Header */}
			{renderCompanyHeader()}

			{/* Main Menu Items */}
			<Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
				<List sx={{ padding: 0 }}>
					{mainMenuItems.map(item => (
						<React.Fragment key={item.key}>
							{renderMenuItem(item)}
							{renderSubmenu(item, 1)}
						</React.Fragment>
					))}
				</List>
			</Box>

			<Divider
				variant="middle"
				sx={{ borderColor: zoullooColors.border.light, marginY: "10px" }}
			/>

			{/* Bottom Menu Items */}
			<Box
				sx={{
					marginTop: "auto",
					transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
				}}
			>
				<List
					sx={{
						p: 0,
						display: "flex",
						justifyContent: "center",
						flexDirection: isCollapsed ? "column" : "row",
						transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
						minHeight: isCollapsed ? "200px" : "auto",
						"& .MuiListItem-root": {
							transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
							transform: isCollapsed
								? "translateY(0) scale(1)"
								: "translateX(0) scale(1)",
							opacity: 1,
							width: "100%",
							justifyContent: "center"
						},
						"&:hover .MuiListItemIcon-root": {
							borderRadius: "7px"
						}
					}}
				>
					{bottomMenuItems.map(item => renderMenuItem(item, undefined, true))}
				</List>

				{/* User Profile Section */}
				{renderUserProfileSection()}
			</Box>
		</Box>
	);

	return (
		<Drawer
			variant={variant}
			anchor="left"
			open={!isCollapsed}
			onClose={onClose}
			sx={{
				"& .MuiDrawer-paper": {
					width: drawerWidth + 2,
					boxSizing: "border-box",
					border: `1px solid ${zoullooColors.grey[400]}`,
					borderRadius: "16px",
					backgroundColor: zoullooColors.background.paper,
					transition: "width 0.3s ease"
				}
			}}
		>
			{drawerContent}
		</Drawer>
	);
};

export default Menu;
